import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { useT } from '@/i18n/useT';
import { ROUTES } from '@/lib/config';
import { ApiError } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BotNav } from './BotNav';

/**
 * Shared shell for every bot screen.
 *
 * All five screens have the same three failure modes, and each one is a
 * different message rather than a generic error: the customer has no bot yet
 * (they need onboarding, not an error), their bot is starting or unreachable
 * (wait, do not panic), or their subscription no longer entitles them.
 *
 * Handling it once here is what keeps the screens themselves about trading.
 */
export function BotGate<T>({
  query,
  children,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
}) {
  const { t } = useT();

  // A background refetch that fails must NOT blank a screen that already has
  // data. Every bot query polls at 2 s with retry:false, so one 502 during a
  // bot restart, or one dropped packet on a phone, would otherwise swap the
  // dashboard for an error card for a full poll cycle. TanStack keeps the last
  // good data on a failed refetch; render it, with a small "reconnecting" line,
  // and let the next poll clear it. The gate below is only for the case where
  // there is nothing to show at all.
  if (query.isError && query.data !== undefined) {
    return (
      <>
        <p className="bot-stale muted">{t('common.reconnecting')}</p>
        {children(query.data)}
      </>
    );
  }

  if (query.isLoading) {
    return (
      <>
        <Card title={t('dash.title')}>
          <p className="muted">{t('common.loading')}</p>
        </Card>
        <BotNav />
      </>
    );
  }

  if (query.isError) {
    const code = query.error instanceof ApiError ? query.error.code : 'server_error';

    // Not errors: this customer has not created their bot yet, or has created
    // it but not connected a Pocket Option account (the gateway answers
    // `not_linked` rather than proxying to a port nothing listens on). Both are
    // onboarding steps and get the onboarding card, not an error card — before
    // this, a brand-new customer opening the Dashboard saw "your bot is not
    // responding", which reads as an outage.
    if (code === 'no_tenant' || code === 'not_linked') {
      const notLinked = code === 'not_linked';
      return (
        <>
          <Card title={t('bot.title')} subtitle={t('bot.subtitle')}>
            <p className="muted">{notLinked ? t('bot.connectBlurb') : t('bot.createBlurb')}</p>
            <Link to={ROUTES.bot}>
              <Button>{notLinked ? t('bot.connectCta') : t('bot.createCta')}</Button>
            </Link>
          </Card>
          <BotNav />
        </>
      );
    }

    return (
      <>
        <Card title={t('dash.title')}>
          <p className="muted">{t(`error.${code}`)}</p>
          <Button variant="ghost" onClick={() => void query.refetch()}>
            {t('common.refresh')}
          </Button>
        </Card>
        <BotNav />
      </>
    );
  }

  return <>{query.data !== undefined ? children(query.data) : null}</>;
}
